import { Table, Checkbox, InputNumber } from '@teo/components';
import { useState, useEffect, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from './Pagination';
import { useMediaQuery } from 'react-responsive';
import MobileDataTable from './MobileDataTable';
import { parseFilter } from '@/util';
import EmptyTable from './EmptyTable';
import PaginationNew from './PaginationNew';
import { Listbox, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { ChevronDownFilled } from '@teo/components/icons';
import { buildSortQuery } from './sortQuery';

type CheckboxIconMobile = 'default' | 'circle' | 'square';
type Screens = 'mobile' | 'planshet' | 'desktop';

export interface DataTablePropsType {
  columns: Array<{
    addTitleOnMobile?: boolean;
    align?: string;
    id: number | string;
    render?: JSX.Element | string | ((row: any) => JSX.Element | string);
    sortable?: boolean;
    sortId?: string;
    title: string | JSX.Element;
    className?: string;
    classNameDesktop?: string;
    hidden?: boolean;
  }>;
  classTable?: string;
  empty?: {
    template?: number | null | undefined;
    text?: string | null | undefined;
    btn_text?: string | null;
    btnClick?: (...args: any[]) => void;
  };
  setContentRange?: (...args: any[]) => void;
  defaultSort?: Array<{
    desc: boolean;
    id: number | string;
  }>;

  /**
   * Used for normal rendering (hook-based fetch like useGet.bind(...)).
   * Must keep returning whatever your current DataTable expects.
   */
  fetch: (...args: any[]) => any;

  /**
   * NEW: Promise-based fetch for iterating selection (select-all / deselect mode).
   * Should return something axios-like: { data: { data: [] } or { data: [] }, headers: { 'content-range': 'items 0-9/123' } }
   * If not provided, iterateSelectedKeys() will throw in ALL_EXCEPT mode.
   */
  fetchPage?: (args: {
    offset: number;
    count: number;
    sort?: string;
    q?: string;
  }) => Promise<any>;

  filter?: string;
  indent?: (...args: any[]) => number;
  page: number;
  pageSize: number;
  rowPath?: (arg0: any) => string;
  onRowClick?: (arg0: any) => void;
  screensUpdateTable?: Screens;

  selectable?: {
    selected: Set<string | number>;
    setSelected: (...args: any[]) => void;
    setDeselect: (...args: any[]) => void;
    deselect: boolean;
    checkboxIconMobileType?: CheckboxIconMobile;

    /**
     * NEW: caller can capture an iterator API that yields the "effective selection"
     * - INCLUDE mode: yields selected keys
     * - ALL_EXCEPT mode: yields ALL keys from the filtered query, except excluded (selected set) keys
     */
    getSelectedIteratorRef?: (api: {
      iterateSelectedKeys: (opts?: {
        pageSize?: number;
        /**
         * If true, iterate the full filtered set even in INCLUDE mode
         * (usually unnecessary; default false)
         */
        forcePaged?: boolean;
      }) => AsyncGenerator<string | number, void, void>;
      getMode: () => 'INCLUDE' | 'ALL_EXCEPT';
    }) => void;
  };

  selectableRow?: {
    selected: boolean;
    selectedRow: string | number;
    selectedRowColor?: string;
    setSelected: (...args: any[]) => void;
  };

  onSelect?: (selection: Set<string | number> | null) => void; // when selection is null, indicates we go to deselect mode
  onDeSelect?: (selection: Set<string | number> | null) => void; // when deselection is null, indicates we go to select mode
  uniqueKey: string | ((row: any) => string);
}

export const DataTable = (props: DataTablePropsType): JSX.Element => {
  const locOffset = sessionStorage.getItem('offsetLoc');
  const prevPathname = sessionStorage.getItem('prevPathname');
  const { t } = useTranslation();

  const [selectMode, setSelectMode] = useState(true);
  const [offset, setOffset] = useState<any>(
    locOffset && locOffset != '0'
      ? prevPathname == window.location.pathname
        ? +locOffset
        : props.page * props.pageSize
      : props.page * props.pageSize
  );
  const [count, setCount] = useState(props.pageSize);

  const showOptions = [
    {
      value: 10,
      label: '10',
    },
    {
      value: 20,
      label: '20',
    },
    {
      value: 40,
      label: '40',
    },
    {
      value: 80,
      label: '80',
    },
  ];

  const [showType, setShowType] = useState(showOptions[1]);

  useEffect(() => {
    if (props?.filter) {
      if (props?.filter?.trim().length > 0) {
        setOffset(0);
      }
    }
  }, [props.filter]);

  useEffect(() => {
    const locOffset = sessionStorage.getItem('offsetLoc');
    const prevPathname = sessionStorage.getItem('prevPathname');

    if (locOffset) {
      if (prevPathname == window.location.pathname) {
        sessionStorage.removeItem('offsetLoc');
        sessionStorage.removeItem('prevPathname');
      }
    }
  }, []);

  const [data, setData] = useState<any[]>([]);
  const [loadData, setLoadData] = useState(false);
  const [maxPage, setMaxPage] = useState(1);

  const [sorted, setSorted] = useState(props.defaultSort || []);
  const [sortQuery, setSortQuery] = useState(
    buildSortQuery(props.defaultSort || [], props.columns)
  );

  const page = Math.floor(offset / count) + 1;

  const addSort = (colId: number | string) => {
    const index = sorted.findIndex((x) => x.id === colId);
    if (index > -1) {
      const sort = sorted[index];
      sorted.splice(index, 1);
      sorted.unshift({ id: colId, desc: !sort.desc });
    } else {
      sorted.unshift({ id: colId, desc: true });
    }
    setSorted([...sorted]);
    setSortQuery(buildSortQuery(sorted, props.columns));
  };

  const defaultRender = (row: any, col: any) => row[col.id];

  const qValue = useMemo(() => parseFilter(props.filter), [props.filter]);

  const result: any = props.fetch({
    offset: locOffset && +locOffset == 0 ? locOffset : offset,
    count,
    sort: sortQuery ? sortQuery : undefined,
    q: qValue,
  });

  const screenUpdate = (screensUpdateTable: string | null) => {
    switch (screensUpdateTable) {
      case 'mobile':
        return '639px';
      case 'planshet':
        return '1199px';
      case 'desktop':
        return '1279px';
      default:
        return '1023px';
    }
  };

  const isDesktopOrLaptop = useMediaQuery({
    query: `(max-width: ${
      props?.screensUpdateTable
        ? screenUpdate(props?.screensUpdateTable)
        : '1023px'
    })`,
  });

  useEffect(() => {
    if (result?.data) {
      setData(result?.data?.data);
      const contentRange = result?.data?.headers?.['content-range']
        ?.split(' ')?.[1]
        ?.split('/');

      const maxCount = contentRange ? Math.ceil(contentRange?.[1]) : 1;
      setMaxPage(Math.ceil(maxCount / count));
      props?.setContentRange && props?.setContentRange(contentRange?.[1]);
      setLoadData(true);
    }
  }, [result?.data, count, props]);

  const navigate = useNavigate();

  const goRouteId = (url: any) => {
    if (offset > 0) {
      sessionStorage.setItem('offsetLoc', offset);
      sessionStorage.setItem('prevPathname', window.location.pathname);
    }
    navigate(url);
  };

  const handleHeaderSelect = () => {
    if (props?.selectable?.selected) {
      if (props?.selectable?.selected?.size > 0) {
        if (props?.selectable?.deselect) {
          props?.selectable?.setDeselect(!props?.selectable?.deselect);
          props?.selectable?.setSelected(new Set());
        } else {
          props?.selectable?.setSelected(new Set());
        }
      } else {
        props?.selectable?.setDeselect(!props?.selectable?.deselect);
        props?.selectable?.setSelected(new Set());
      }
    }
  };

  const handleItemSelect = (key: any) => {
    const newSelected = new Set(props?.selectable?.selected);
    if (
      (newSelected.has(key) && selectMode) ||
      (!newSelected.has(key) && !selectMode)
    ) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    props?.selectable?.setSelected(newSelected);
  };

  // ===== NEW: Selection iterator API =====

  const getRowKey = (row: any): string | number => {
    return typeof props.uniqueKey === 'function'
      ? props.uniqueKey(row)
      : row[props.uniqueKey];
  };

  const parseTotalFromContentRange = (contentRange?: string) => {
    // expects e.g. "items 0-9/123" -> 123
    const totalStr = contentRange?.split(' ')?.[1]?.split('/')?.[1];
    const total = totalStr != null ? Number(totalStr) : NaN;
    return Number.isFinite(total) ? total : null;
  };

  const iterateSelectedKeys = async function* (opts?: {
    pageSize?: number;
    forcePaged?: boolean;
  }): AsyncGenerator<string | number, void, void> {
    const selectedSet = props?.selectable?.selected ?? new Set();
    const isDeselectMode = !!props?.selectable?.deselect;

    // INCLUDE mode: selectedSet is the selection
    if (!isDeselectMode && !opts?.forcePaged) {
      for (const k of selectedSet) yield k;
      return;
    }

    // For ALL_EXCEPT (select-all / deselect mode), we must page through the full filtered query.
    if (!props.fetchPage) {
      throw new Error(
        'DataTable: selectable.getSelectedIteratorRef used in ALL_EXCEPT mode, but fetchPage is not provided.'
      );
    }

    const pageSize = opts?.pageSize ?? 200;
    let localOffset = 0;
    let total: number | null = null;

    while (total == null || localOffset < total) {
      const pageResult = await props.fetchPage({
        offset: localOffset,
        count: pageSize,
        sort: sortQuery ? sortQuery : undefined,
        q: qValue,
      });

      const rows =
        pageResult?.data?.data ??
        pageResult?.data ??
        pageResult?.data?.rows ??
        [];

      if (!Array.isArray(rows) || rows.length === 0) break;

      const contentRangeHeader =
        pageResult?.headers?.['content-range'] ??
        pageResult?.data?.headers?.['content-range']; // tolerate axios-like wrappers

      const parsedTotal = parseTotalFromContentRange(contentRangeHeader);
      if (parsedTotal != null) total = parsedTotal;

      for (const row of rows) {
        const key = getRowKey(row);

        // INCLUDE mode but paged: yield only selected keys
        if (!isDeselectMode && opts?.forcePaged) {
          if (selectedSet.has(key)) yield key;
          continue;
        }

        // ALL_EXCEPT mode: selectedSet = excluded keys; yield everything else
        if (!selectedSet.has(key)) yield key;
      }

      localOffset += pageSize;
    }
  };

  useEffect(() => {
    props?.selectable?.getSelectedIteratorRef?.({
      iterateSelectedKeys,
      getMode: () => (props?.selectable?.deselect ? 'ALL_EXCEPT' : 'INCLUDE'),
    });
    // The iterator depends on current query + sort + selection state
  }, [
    props?.selectable?.getSelectedIteratorRef,
    props?.selectable?.deselect,
    props?.selectable?.selected,
    sortQuery,
    qValue,
  ]);

  // ===== render =====

  return (
    <>
      {loadData &&
        (data?.length > 0 ? (
          <>
            <div
              className={`${props?.classTable} ${
                !isDesktopOrLaptop && 'border'
              } rounded-lg border-grey-02 text-green-500`}
            >
              {!isDesktopOrLaptop ? (
                <Table>
                  <Table.Header>
                    {props?.selectable?.selected && data?.length > 0 && (
                      <th
                        className="text-sm-medium w-11 py-2 pl-3 text-left text-black"
                        scope="col"
                      >
                        <Checkbox
                          checked={
                            (props?.selectable?.deselect &&
                              props?.selectable?.selected.size <
                                data?.length - 1) ||
                            (props?.selectable?.selected.size >
                              data?.length - 1 &&
                              !props?.selectable?.deselect)
                          }
                          id="test-box"
                          onChange={handleHeaderSelect}
                          indeterminate={
                            props?.selectable?.selected.size !== data?.length &&
                            props?.selectable?.selected.size > 0 &&
                            props?.selectable?.selected.size < data?.length
                              ? true
                              : false
                          }
                        />
                      </th>
                    )}
                    {props.columns.map((col: any) => {
                      if (col?.hidden) return null;
                      return (
                        <Table.HeaderCell
                          key={col.id}
                          onSort={
                            col.sortable === undefined || col.sortable
                              ? () => addSort(col.id)
                              : undefined
                          }
                          sortDirection={
                            sorted[0]?.id === col.id
                              ? sorted[0]?.desc
                                ? 'ascending'
                                : 'descending'
                              : undefined
                          }
                          align={col.align || 'left'}
                        >
                          {typeof col?.title === 'function'
                            ? col.title()
                            : col.title}
                        </Table.HeaderCell>
                      );
                    })}
                  </Table.Header>
                  <Table.Body>
                    {data?.map((row: any, i) => {
                      const key = getRowKey(row);
                      const rowPath = props?.rowPath?.(row);
                      const rowID = row?.id || row?.studentId;
                      return (
                        <tr
                          key={String(key) + '_' + i}
                          className={`h-16 text-sm text-black hover:bg-grey-01 ${
                            (rowPath ||
                              props?.selectableRow?.selected ||
                              props.onRowClick) &&
                            'hover:cursor-pointer'
                          } ${
                            rowID &&
                            props?.selectableRow?.selectedRow == rowID &&
                            (props?.selectableRow?.selectedRowColor
                              ? props?.selectableRow?.selectedRowColor
                              : 'bg-grey-01')
                          }`}
                          onClick={() => {
                            if (props.onRowClick) props.onRowClick(row);
                            if (props?.selectableRow?.selected)
                              props?.selectableRow?.setSelected(rowID);
                            if (rowPath) goRouteId(rowPath);
                            return;
                          }}
                          data-testid={`row-${rowPath}`}
                        >
                          {props?.selectable?.selected && (
                            <td
                              className="text-sm-medium w-11 py-2 pl-3 text-left text-black"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                            >
                              <Checkbox
                                checked={
                                  !props?.selectable?.deselect
                                    ? props?.selectable?.selected.has(key)
                                      ? true
                                      : false
                                    : props?.selectable?.selected.has(key)
                                    ? false
                                    : true
                                }
                                key={
                                  String(key) +
                                  '_' +
                                  props?.selectable?.selected.has(key)
                                }
                                onChange={() => handleItemSelect(key)}
                              />
                            </td>
                          )}
                          {props.columns.map((col: any) => {
                            if (col?.hidden) return null;
                            const content = col?.render
                              ? typeof col?.render === 'function'
                                ? col.render(row)
                                : col.render
                              : defaultRender(row, col);
                            return (
                              <Table.Cell
                                key={`${row?.id ?? key}_${col.id}` as string}
                                align={col.align || 'left'}
                                className={col.classNameDesktop || ''}
                              >
                                {content}
                              </Table.Cell>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </Table.Body>
                </Table>
              ) : (
                <MobileDataTable
                  setings={props}
                  data={data}
                  defaultRender={defaultRender}
                  handleItemSelect={handleItemSelect}
                  goRouteId={goRouteId}
                  props={props}
                />
              )}
            </div>
            {maxPage > 1 && (
              <div className="flex w-full items-center justify-between">
                <div>
                  <PaginationNew
                    setOffset={setOffset}
                    offset={offset}
                    count={count}
                    page={page}
                    maxPage={maxPage}
                  />
                </div>
                <div className="flex items-center">
                  <div className="ml-2 hidden items-center gap-1 lg:flex">
                    <p className="mr-1 text-sm text-grey-07">
                      {t('pagination.go_to_page')}
                    </p>
                    <InputNumber
                      mode="integer"
                      value={page}
                      onChange={(value: any) => {
                        if (value > 0) {
                          setOffset((value - 1) * count);
                        }
                      }}
                      min={1}
                      max={maxPage}
                      className="max-w-[50px]"
                    />
                  </div>
                  <div className="ml-2 flex items-center gap-1">
                    <p className="mr-1 text-sm text-grey-07">
                      {t('pagination.show')}
                    </p>
                    <div>
                      <Listbox
                        value={showType}
                        onChange={(newShowType) => {
                          setShowType(newShowType);
                          setCount(newShowType?.value);
                          setOffset(
                            Math.floor(offset / newShowType?.value) *
                              newShowType?.value
                          );
                        }}
                      >
                        {({ open }) => (
                          <div>
                            <Listbox.Button
                              className={clsx(
                                'flex items-center justify-between',
                                'rounded-md border border-solid outline outline-2',
                                'min-h-10 w-full min-w-[70px] px-2 text-base',
                                {
                                  'border-secondary-04 bg-white outline-secondary-01':
                                    open,
                                  'border-transparent bg-grey-transparent-01 text-grey-08 outline-transparent hover:border-grey-transparent-03 hover:bg-white hover:outline-grey-transparent-01 focus:border-secondary-04 focus:bg-white focus:outline-secondary-01':
                                    !open,
                                }
                              )}
                            >
                              <span className="mr-2 block truncate">
                                {!showType?.label ? '' : showType?.label}
                              </span>
                              <ChevronDownFilled
                                className={clsx(
                                  'h-5 w-5',
                                  'transition-transform',
                                  open && 'rotate-180'
                                )}
                              />
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition-opacity"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <div className="listbox relative z-10">
                                <Listbox.Options className="absolute bottom-0 mt-1  flex max-h-40 w-full -translate-y-[42px] cursor-pointer flex-col gap-1 overflow-auto rounded-lg bg-white p-1 drop-shadow-s focus:outline-none">
                                  {showOptions.map((option, optionIndex) => (
                                    <Listbox.Option
                                      key={optionIndex}
                                      className={({ active }) =>
                                        clsx(
                                          'rounded-md px-3 py-2 text-base text-black',
                                          active && 'bg-grey-transparent-02'
                                        )
                                      }
                                      value={option}
                                    >
                                      {({ selected }) => (
                                        <span
                                          className={clsx(
                                            'block',
                                            selected && 'font-medium'
                                          )}
                                        >
                                          <span>{option?.label}</span>
                                        </span>
                                      )}
                                    </Listbox.Option>
                                  ))}
                                </Listbox.Options>
                              </div>
                            </Transition>
                          </div>
                        )}
                      </Listbox>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyTable
            template={props?.empty?.template}
            text={props?.empty?.text}
            btn_text={props?.empty?.btn_text}
            btnClick={() => props?.empty?.btnClick && props?.empty?.btnClick()}
            filter={props?.filter}
          />
        ))}
    </>
  );
};

DataTable.defaultProps = {
  page: 0,
  pageSize: 10,
  columns: [],
  uniqueKey: 'id',
};
